import forge from 'apis/forge';
import { IconType } from 'components/Icon';
import SubmitButton, { SubmitButtonState } from 'components/Widget/Button/SubmitButton';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import Pill from 'components/Widget/Pill';
import VoiceInputButton from 'components/Widget/VoiceInputButton';
import {
	ChatStatus,
	DomElement,
	Frame,
	FrameChat,
	UserChatEntry,
} from 'dragonmouse-shared/interfaces';
import { interceptIfNoCredits } from 'economy';
import { useFrameChatQueue } from 'hooks/useFrameChatQueue';
import { useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { canEditFrame } from 'redux/db/util';
import { setActiveFrameChatId, setSelectedDomElements } from 'redux/mode/actions';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { getElementName } from 'utils';
import { useChatInputValidation } from '../../hooks/useChatInputValidation';
import * as Base from '../styles';

interface FrameChatInputProps {
	frame: Frame;
	frameChat: FrameChat;
}

const FrameChatInput = ({ frame, frameChat }: FrameChatInputProps) => {
	const dispatch = useDispatch();
	const [prompt, setPrompt] = useState('');
	const [isFocused, setIsFocused] = useState(false);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	const queue = useFrameChatQueue(frameChat.id);

	const { selectedDomElements } = useSelector(
		(r: RootReducer) => ({
			selectedDomElements: r.mode.selectedDomElements,
		}),
		shallowEqual
	);

	const isStoppable = frameChat.metadata?.status === ChatStatus.STOPPABLE;

	const { canSubmit } = useChatInputValidation({ prompt, loadingAttachments: undefined });

	// Focus input when DOM elements are selected
	useEffect(() => {
		if (selectedDomElements.length > 0 && inputRef.current) {
			inputRef.current.focus();
		}
	}, [selectedDomElements]);

	//---------------------------------------------------------------------------------------------------
	// HANDLERS
	//---------------------------------------------------------------------------------------------------
	const onSubmit = async () => {
		const trimmed = prompt.trim();
		if (!trimmed) return;

		if (!canEditFrame(frame)) {
			displayViewOnlyModal();
			return;
		}

		if (interceptIfNoCredits({ kind: 'chatEdit', sceneId: frame.sceneId })) {
			return;
		}

		const entry: UserChatEntry = {
			kind: 'user',
			message: trimmed,
		};

		const domElements = selectedDomElements.length > 0 ? selectedDomElements : undefined;

		setPrompt('');
		dispatch(setSelectedDomElements([]));

		if (frameChat.id) {
			// Chat exists — queue handles concurrency with busy chat
			queue.enqueue({
				label: trimmed,
				payload: {
					kind: 'addUserEntry',
					entry,
					frameId: frame.id,
					selectedDomElements: domElements,
				},
			});
		} else {
			// First message — no chat yet, call forge directly to create it
			try {
				const resp = await forge.addFrameChatUserEntry.withToast()({
					frameId: frame.id,
					entry,
					selectedDomElements: domElements,
				});
				if (resp?.frameChatId) {
					dispatch(setActiveFrameChatId(resp.frameChatId));
				}
			} catch {
				// error surfaced by withToast
			}
		}
	};

	const onStop = async () => {
		try {
			await forge.stopFrameChatGeneration({ frameId: frame.id });
		} catch {
			// best effort
		}
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			if (canSubmit) {
				onSubmit();
			}
		}
	};

	// Dictation appends the finished transcript to whatever was typed before
	// recording began.
	const promptAtVoiceStartRef = useRef('');
	const onVoiceStart = () => {
		promptAtVoiceStartRef.current = prompt;
	};
	const onVoiceText = (text: string) => {
		const base = promptAtVoiceStartRef.current.trimEnd();
		setPrompt(base ? `${base} ${text}` : text);
	};

	//---------------------------------------------------------------------------------------------------
	// RENDERING
	//---------------------------------------------------------------------------------------------------
	const renderActions = () => {
		return (
			<Base.Actions>
				<Base.Spacer />
				<VoiceInputButton onRecordStart={onVoiceStart} onTranscript={onVoiceText} />
				<Base.SubmitButtonWrapper>
					<SubmitButton
						state={
							isStoppable || canSubmit ? SubmitButtonState.REGULAR : SubmitButtonState.DISABLED
						}
						type={isStoppable ? IconType.STOP : IconType.LINE_ARROW_UP}
						tooltip={isStoppable ? 'Stop generation' : 'Submit edit'}
						tooltipPlacement="bottom-start"
						onClick={() => (isStoppable ? onStop() : onSubmit())}
					/>
				</Base.SubmitButtonWrapper>
			</Base.Actions>
		);
	};

	return (
		<Base.Base>
			<Base.Prompt isFocused={isFocused}>
				{selectedDomElements.length > 0 && (
					<Base.DsPicks>
						{selectedDomElements.map((element: DomElement) => (
							<Pill key={element.id} label={getElementName(element)} />
						))}
					</Base.DsPicks>
				)}
				<Base.MultilineInputWrapper>
					<MultilineInput
						data-chat-input="true"
						ref={inputRef}
						value={prompt}
						onChange={(e) => setPrompt(e.currentTarget.value)}
						onKeyDown={onKeyDown}
						placeholder="What would you like to change?"
						minLines={2}
						renderBorder={false}
						onFocusChange={setIsFocused}
					/>
				</Base.MultilineInputWrapper>
				{renderActions()}
			</Base.Prompt>
		</Base.Base>
	);
};

export default FrameChatInput;
